import React, { useState, useEffect } from "react";
import "./DynamicNationalIdentityTable.css";

interface NationalIdentityEntry {
  id: string;
  countryOfIssue: string;
  nationalIdNumber: string;
  issueDate: string;
  expiryDate: string;
}

interface DynamicNationalIdentityTableProps {
  value: NationalIdentityEntry[];
  onChange: (entries: NationalIdentityEntry[]) => void;
  required?: boolean;
  hasNationalId?: string; // To control visibility
}

const DynamicNationalIdentityTable: React.FC<
  DynamicNationalIdentityTableProps
> = ({ value, onChange, required = false, hasNationalId = "No" }) => {
  // Safety check: ensure value is always an array
  const safeValue = Array.isArray(value) ? value : [];
  const [entries, setEntries] = useState<NationalIdentityEntry[]>(safeValue);

  useEffect(() => {
    // Update entries when value prop changes
    const newSafeValue = Array.isArray(value) ? value : [];
    setEntries(newSafeValue);
  }, [value]);

  useEffect(() => {
    onChange(entries);
  }, [entries, onChange]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === "Enter") {
        addEntry();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const addEntry = () => {
    const newEntry: NationalIdentityEntry = {
      id: `national_id_${Date.now()}_${Math.random()}`,
      countryOfIssue: "",
      nationalIdNumber: "",
      issueDate: "",
      expiryDate: "",
    };

    const updatedEntries = [...entries, newEntry];
    setEntries(updatedEntries);
    onChange(updatedEntries);
  };

  const removeEntry = (id: string) => {
    const updatedEntries = entries.filter((entry) => entry.id !== id);
    setEntries(updatedEntries);
    onChange(updatedEntries);
  };

  const updateEntry = (
    id: string,
    field: keyof NationalIdentityEntry,
    value: string
  ) => {
    const updatedEntries = entries.map((entry) =>
      entry.id === id ? { ...entry, [field]: value } : entry
    );
    setEntries(updatedEntries);
    onChange(updatedEntries);
  };

  // Initialize with at least one entry if hasNationalId is "Yes" and no entries exist
  useEffect(() => {
    if (hasNationalId === "Yes" && entries.length === 0) {
      addEntry();
    }
  }, [hasNationalId]);

  // Hide the table if hasNationalId is not "Yes"
  if (hasNationalId !== "Yes") {
    return null;
  }

  return (
    <div className="national-identity-table">
      <div className="table-header">
        <h4>National Identity Details</h4>
        <button
          type="button"
          className="add-btn"
          onClick={addEntry}
          title="Add national identity entry (Ctrl+Enter)"
        >
          + Add National ID Entry
        </button>
      </div>

      {entries.length === 0 ? (
        <div className="empty-state">
          <p>No national identity information entered yet.</p>
          <button type="button" className="add-btn" onClick={addEntry}>
            + Add National ID Entry
          </button>
        </div>
      ) : (
        <div className="entries-container">
          {entries.map((entry, index) => (
            <div key={entry.id} className="entry-card">
              <div className="entry-header">
                <div className="entry-number">
                  <span className="entry-badge">{index + 1}</span>
                  <h5>Entry {index + 1}</h5>
                </div>
                <button
                  type="button"
                  className="remove-btn"
                  onClick={() => removeEntry(entry.id)}
                  title="Remove this national identity entry"
                >
                  ×
                </button>
              </div>

              <div className="entry-form">
                <div className="form-row form-row-1">
                  <div className="field-group">
                    <label>Country of Issue</label>
                    <input
                      type="text"
                      value={entry.countryOfIssue}
                      onChange={(e) =>
                        updateEntry(entry.id, "countryOfIssue", e.target.value)
                      }
                      placeholder="e.g., Germany"
                    />
                  </div>

                  <div className="field-group">
                    <label>National Identity Number</label>
                    <input
                      type="text"
                      value={entry.nationalIdNumber}
                      onChange={(e) =>
                        updateEntry(
                          entry.id,
                          "nationalIdNumber",
                          e.target.value
                        )
                      }
                      placeholder="e.g., 36501-3668150-3"
                    />
                  </div>
                </div>

                <div className="form-row form-row-2">
                  <div className="field-group">
                    <label>Issue Date</label>
                    <input
                      type="date"
                      value={entry.issueDate}
                      onChange={(e) =>
                        updateEntry(entry.id, "issueDate", e.target.value)
                      }
                      placeholder="yyyy-mm-dd"
                    />
                  </div>

                  <div className="field-group">
                    <label>Expiry Date</label>
                    <input
                      type="date"
                      value={entry.expiryDate}
                      onChange={(e) =>
                        updateEntry(entry.id, "expiryDate", e.target.value)
                      }
                      placeholder="yyyy-mm-dd"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {entries.length > 0 && (
        <div className="table-footer">
          <button
            type="button"
            className="add-btn"
            onClick={addEntry}
            title="Add another national identity entry (Ctrl+Enter)"
          >
            + Add Another National ID Entry
          </button>
        </div>
      )}
    </div>
  );
};

export default DynamicNationalIdentityTable;
